import { VEHICLE_POLICY_FIELDS } from './vehiclePolicyConfig';

/** @typedef {'inh' | 'cust' | 'lock' | null} FieldBadge */

const SPECIFIC_TOOLTIPS = {
    __presetName__: {
        title: 'Vehicle preset name',
        desc: 'A name for this preset (e.g. fleet segment or model line). Used when assigning the preset to assets.',
    },
    __newVehicleType__: {
        title: 'New vehicle type',
        desc: 'Adds a vehicle type for every agency to use. Types cannot be renamed or deleted afterwards — only deactivated — because presets and vehicles store the name itself.',
    },
    __vehicleType__: {
        title: 'Vehicle type',
        desc: 'Vehicle classification used for analytics and industry fuel benchmarking.',
    },
    __description__: {
        title: 'Description',
        desc: 'Optional internal note for operators. Does not change device behaviour.',
    },
    __activePrice__: {
        title: 'Active price (resolved)',
        desc: 'Fuel or energy unit price resolved from the central registry for the selected fuel type and effective date.',
    },
    __vibrationGroup__: {
        title: 'Vibration sensitivity (X / Y / Z)',
        desc: 'Axis-specific G-force thresholds for vibration and harsh-event detection. Lower values are more sensitive.',
    },
    __fuelCalibrationMap__: {
        title: 'Fuel calibration map',
        desc: 'Hardware table mapping raw sensor percentage to litres. Maintained by administrators; not editable in this view.',
    },
    'Maximum Tank Capacity': {
        title: 'Tank capacity',
        desc: 'Maximum tank volume in litres. Used to convert level readings into volume for reporting and alerts.',
    },
    'Minimum Refill Detection': {
        title: 'Refill threshold',
        desc: 'Minimum volume increase required to register a refill event and stitch fuel accounting after a stop.',
    },
    'Theft/Drain Detection Alert': {
        title: 'Theft / drain threshold',
        desc: 'Minimum fuel drop while ignition is off before a theft or suspicious drain alert is raised.',
    },
    'Sensor Noise/Spike Filter (L)': {
        title: 'Spike filter',
        desc: 'Maximum single-step drop to ignore as sensor noise or slosh while the vehicle is moving.',
    },
    'Fuel Stabilization Wait Time (Sec)': {
        title: 'Slosh settle duration',
        desc: 'Wait time for the level to stabilise before confirming refill, drain, or theft states.',
    },
    'Allowed Stabilization Fluctuation (L)': {
        title: 'Slosh tolerance',
        desc: 'Allowed wobble in litres during the stabilisation window before the reading is considered stable.',
    },
    'Micro-refill (L)': {
        title: 'Micro-refill',
        desc: 'Minimum stationary top-up below the main refill threshold. Leave blank to keep detection off.',
    },
    'Offline gap cap (min)': {
        title: 'Offline gap cap',
        desc: 'Maximum minutes from each telemetry gap counted toward engine and idle duration.',
    },
    'Drain window (min)': {
        title: 'Drain window',
        desc: 'Sliding period used to distinguish sustained drain or theft from normal fuel burn.',
    },
    'Max burn rate (L/hr)': {
        title: 'Max burn rate',
        desc: 'Fastest physically plausible running fuel burn; faster sustained drops are classified as drains.',
    },
    'Weak Battery Cranking Alert (Volts)': {
        title: 'Crank warning threshold',
        desc: 'Minimum voltage seen during cranking that triggers a weak-battery warning before outright failure.',
    },
    'Cranking Battery Failure Limit (Volts)': {
        title: 'Crank failure threshold',
        desc: 'Voltage below which the starter battery is treated as failed or unable to start the engine reliably.',
    },
    'Max Parked Parasitic Drain (V/hr)': {
        title: 'Parasitic drain limit',
        desc: 'Maximum allowed parked voltage decay rate before a parasitic drain diagnostic is raised.',
    },
};

const defaultDescForKey = (fieldKey) => (
    `Sets ${fieldKey.replace(/\s*\([^)]*\)\s*$/g, '').trim()} for this vehicle preset.`
);

/** @param {string} fieldKeyOrSpecial */
export const getFieldTooltip = (fieldKeyOrSpecial) => {
    if (SPECIFIC_TOOLTIPS[fieldKeyOrSpecial]) {
        return SPECIFIC_TOOLTIPS[fieldKeyOrSpecial];
    }
    return {
        title: fieldKeyOrSpecial,
        desc: defaultDescForKey(fieldKeyOrSpecial),
    };
};

/** Baseline policy values shown on “reset” for demo / typical override fields */
export const POLICY_BASELINE_VALUE = {
    'Minimum Refill Detection': '15',
    'Cranking Battery Failure Limit (Volts)': '20',
    'Receipt Calibration Multiplier': '1',
};

/** Initial badge per field (null = no badge). Mirrors typical inherited vs client override vs admin-locked patterns. */
export const INITIAL_FIELD_BADGE = VEHICLE_POLICY_FIELDS.reduce((acc, { fieldKey }) => {
    acc[fieldKey] = null;
    return acc;
}, {});

Object.assign(INITIAL_FIELD_BADGE, {
    'Maximum Tank Capacity': 'inh',
    'Minimum Refill Detection': 'cust',
    'Theft/Drain Detection Alert': 'inh',
    'Sensor Noise/Spike Filter (L)': 'inh',
    'Fuel Stabilization Wait Time (Sec)': 'inh',
    'Allowed Stabilization Fluctuation (L)': 'inh',
    'Weak Battery Cranking Alert (Volts)': 'inh',
    'Cranking Battery Failure Limit (Volts)': 'cust',
    'Alternator Charging Minimum (Volts)': 'inh',
    'Alternator Failure Wait Time (Sec)': 'inh',
    'Max Parked Parasitic Drain (V/hr)': 'inh',
    'Battery System Architecture': 'inh',
    'Receipt Calibration Multiplier': 'cust',
    'Overspeeding Wind Resistance Penalty (%)': 'inh',
});
